import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Heading, Link, spacing } from 'component-library';
import {
  getCountryFromCompanyId,
  getGeneralTermsAndConditionsLink,
  getPrivacyPolicyLink,
  getWaiverLink,
} from '@sr/utils';
import { type Country, type SubscriptionType } from '@sr/types';
import { useEventTracking } from 'core/lib/hooks/useEventTracking/useEventTracking';
import { Wizard, type WizardStepType } from 'component-library/components/Navigation/Wizard';
import {
  type CheckoutFn,
  type OrderDetailsState,
  type SelectHierarchyFn,
  type SelectInvoiceRecipientFn,
  type SelectUserFn,
  type ApiHooks,
} from '../types';
import { CheckboxLabel, FinalStepWrapper, StepContainer } from '../styles';
import SelectUserAndHierarchy from './SelectUsersAndHierarchy';
import SelectInvoiceRecipient from './SelectInvoiceRecipient';
import useAvailableSubscriptionHierarchies from './utils/useAvailableSubscriptionHierarchies';
import validateOrderDetails from './utils/validateOrderDetails';
import { BoxContainer } from './utils/BoxContainer';

const defaultOrderState: OrderDetailsState = {
  user: undefined,
  selectedLocation: undefined,
  selectedHierarchy: undefined,
  invoiceRecipient: undefined,
  terms: false,
  market: undefined,
};

type CheckoutFormProps = {
  submitOrder: CheckoutFn;
  country: Country;
  subscriptionType: SubscriptionType;
  apiHooks: ApiHooks;
  s3AssetsUrl: string;
  onCountryChange?: (country: Country) => void;
  onHierarchyChange?: (hierarchy?: string) => void;
  hasEnterprisePartner?: boolean;
  isCheckingEnterprise?: boolean;
  reset?: () => void;
  hideTitle?: boolean;
};

export const CheckoutForm: React.FC<CheckoutFormProps> = (props) => {
  const {
    submitOrder,
    country,
    subscriptionType,
    apiHooks,
    s3AssetsUrl,
    onCountryChange,
    onHierarchyChange,
    hasEnterprisePartner,
    isCheckingEnterprise,
    reset,
    hideTitle,
  } = props;

  const [orderDetails, setOrderDetails] = useState<OrderDetailsState>(defaultOrderState);
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(0);
  const { trackEvent, trackVirtualPageView } = useEventTracking();
  const [trackedStep1, setTrackedStep1] = useState(false);
  const [trackedStep2, setTrackedStep2] = useState(false);
  const [trackedStep3, setTrackedStep3] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(country);
  const [hasAvailableCostCenters, setHasAvailableCostCenters] = useState(false);
  const [hasReachedStep2, setHasReachedStep2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    details: hierarchyDetails,
    hierarchies,
    loading: hierarchiesLoading,
  } = useAvailableSubscriptionHierarchies(apiHooks);

  // Filter hierarchies by selected country
  const filteredHierarchyDetails = useMemo(() => {
    if (!hierarchyDetails) return undefined;

    return hierarchyDetails.filter((hierarchy) => {
      const companyId = Number(hierarchy.dwKey.split('_')[0]);

      return getCountryFromCompanyId(companyId) === selectedCountry;
    });
  }, [hierarchyDetails, selectedCountry]);

  // Auto-select country if only one country has available locations
  useEffect(() => {
    if (!hierarchyDetails || hierarchiesLoading) return;

    const countriesWithLocations = new Set(
      hierarchyDetails.map((h) => getCountryFromCompanyId(Number(h.dwKey.split('_')[0]))),
    );

    if (countriesWithLocations.size === 1) {
      const [singleCountry] = Array.from(countriesWithLocations);

      if (singleCountry !== selectedCountry) {
        setSelectedCountry(singleCountry);
        if (onCountryChange) {
          onCountryChange(singleCountry);
        }
      }
    }
  }, [hierarchyDetails, hierarchiesLoading, selectedCountry, onCountryChange]);

  // Update selected country when country prop changes
  useEffect(() => {
    if (country !== selectedCountry) {
      setSelectedCountry(country);
    }
  }, [country, selectedCountry]);

  const handleUserSelect: SelectUserFn = useCallback(
    (usr) => {
      setOrderDetails((prev) => ({ ...prev, user: usr }));
    },
    [setOrderDetails],
  );

  const handleHierarchySelect: SelectHierarchyFn = useCallback(
    (dwKey) => {
      if (dwKey) {
        setSelectedCountry(getCountryFromCompanyId(Number(dwKey.split('_')[0])));
      }
      setOrderDetails((prev) => ({
        ...prev,
        market: getCountryFromCompanyId(dwKey ? Number(dwKey.split('_')[0]) : selectedCountry),
        selectedLocation: dwKey,
        selectedHierarchy:
          dwKey && hierarchies
            ? hierarchies.find(({ dwKeys }) => dwKeys.includes(dwKey))
            : undefined,
      }));

      // Notify parent of hierarchy change
      if (onHierarchyChange) {
        onHierarchyChange(dwKey);
      }
    },
    [setOrderDetails, hierarchies, selectedCountry, onHierarchyChange],
  );

  const handleInvoiceRecipientSelect: SelectInvoiceRecipientFn = useCallback(
    (recipient) => {
      setOrderDetails((prev) => ({ ...prev, invoiceRecipient: recipient }));
    },
    [setOrderDetails],
  );

  useEffect(() => {
    if (isSubmitting) return;
    handleInvoiceRecipientSelect(undefined);
    setStep(0);
  }, [orderDetails.user?.id, handleInvoiceRecipientSelect, isSubmitting]);

  const handleTermsToggle: () => void = useCallback(() => {
    setOrderDetails((prev) => ({ ...prev, terms: !prev.terms }));
  }, [setOrderDetails]);

  const isCurrentStepValid = useMemo(
    () => validateOrderDetails(step + 1, orderDetails, hasAvailableCostCenters),
    [step, orderDetails, hasAvailableCostCenters],
  );

  const handleChangeStep = useCallback(
    (newStep: number) => {
      const isNext = newStep > step;

      if (isNext && isCurrentStepValid) {
        setStep(newStep);
        if (newStep >= 1) {
          setHasReachedStep2(true);
        }
      } else if (!isNext) {
        setStep(newStep);
      }
    },
    [step, isCurrentStepValid],
  );

  const handleSubmit = useCallback(() => {
    if (isCurrentStepValid) {
      setIsSubmitting(true);
      submitOrder(orderDetails);
    }

    return Promise.resolve();
  }, [orderDetails, isCurrentStepValid, submitOrder]);

  useEffect(() => {
    const parentPageTitle = t('pagetitle', `${subscriptionType} Checkout`);

    if (step === 0 && !trackedStep1) {
      const pageTitle = t('step1.progressbar', { lng: 'en' });
      const fullPageTitle = `${parentPageTitle} - ${pageTitle}`;

      trackVirtualPageView(
        fullPageTitle,
        `/${subscriptionType.toLowerCase()}/checkout/step-${step + 1}`,
      );

      setTrackedStep1(true);
      setTrackedStep2(false);
      setTrackedStep3(false);
    }

    if (step === 1 && !trackedStep2) {
      const pageTitle = t('step2.progressbar', { lng: 'en' });
      const fullPageTitle = `${parentPageTitle} - ${pageTitle}`;

      if (reset) {
        reset();
      }

      trackVirtualPageView(
        fullPageTitle,
        `/${subscriptionType.toLowerCase()}/checkout/step-${step + 1}`,
      );

      setTrackedStep1(false);
      setTrackedStep2(true);
      setTrackedStep3(false);
    }

    if (step === 2 && !trackedStep3) {
      const pageTitle = t('step3.progressbar', { lng: 'en' });
      const fullPageTitle = `${parentPageTitle} - ${pageTitle}`;

      trackVirtualPageView(
        fullPageTitle,
        `/${subscriptionType.toLowerCase()}/checkout/step-${step + 1}`,
      );

      setTrackedStep1(false);
      setTrackedStep2(false);
      setTrackedStep3(true);
    }
  }, [
    step,
    t,
    trackVirtualPageView,
    trackedStep1,
    trackedStep2,
    trackedStep3,
    subscriptionType,
    reset,
  ]);

  useEffect(() => {
    if (orderDetails.selectedLocation && !hasAvailableCostCenters) {
      trackEvent('subscription_order_no_available_cost_center');
    }
  }, [orderDetails.selectedLocation, hasAvailableCostCenters, trackEvent]);

  const nextStepDisabled = useMemo(() => {
    if (!isCurrentStepValid) return true;

    if (step === 0 && subscriptionType === 'CSRD_PREMIUM') {
      return isCheckingEnterprise === true || hasEnterprisePartner === true;
    }

    return false;
  }, [isCurrentStepValid, step, subscriptionType, isCheckingEnterprise, hasEnterprisePartner]);

  const wizardSteps: WizardStepType[] = [
    {
      title: t('step1.title'),
      stepTitle: t('step1.progressbar'),
      component: (
        <StepContainer data-testid="step1-users-and-locations">
          {!hideTitle && (
            <Heading variant="small" as="h3" mb={spacing.small}>
              {t('step1.title')}
            </Heading>
          )}
          <SelectUserAndHierarchy
            onUserChange={handleUserSelect}
            onHierarchyChange={handleHierarchySelect}
            hierarchiesLoading={hierarchiesLoading}
            hierarchies={filteredHierarchyDetails}
            hasAvailableCostCenters={hasReachedStep2 ? hasAvailableCostCenters : true}
            subscriptionType={subscriptionType}
            hasEnterprisePartner={hasEnterprisePartner}
          />
        </StepContainer>
      ),
    },
    {
      title: t('step2.title'),
      stepTitle: t('step2.progressbar'),
      component: (
        <StepContainer data-testid="step2-invoice-partner">
          {!hideTitle && (
            <Heading variant="small" as="h3" mb={spacing.small}>
              {t('step2.title')}
            </Heading>
          )}
          <SelectInvoiceRecipient
            hierarchiesLoading={hierarchiesLoading}
            onRecipientChange={handleInvoiceRecipientSelect}
            onAvailableCostCentersChange={setHasAvailableCostCenters}
            selectedHierarchy={orderDetails.selectedLocation}
            getPartnerInfo={apiHooks.getPartnerInfo}
          />
        </StepContainer>
      ),
    },
    {
      title: t('step3.title'),
      stepTitle: t('step3.progressbar'),
      component: (
        <StepContainer data-testid="step3-terms-condition">
          {!hideTitle && (
            <Heading variant="small" as="h3" mb={spacing.small}>
              {t('step3.title')}
            </Heading>
          )}
          <BoxContainer>
            <FinalStepWrapper>
              <Checkbox
                checked={orderDetails.terms}
                onChange={handleTermsToggle}
                data-testid="subscription-terms-checkbox"
              >
                <CheckboxLabel>
                  {t('step3.label')}&nbsp;
                  <Link
                    target="_blank"
                    href={getWaiverLink(subscriptionType, orderDetails.market, s3AssetsUrl)}
                    underline={true}
                  >
                    {t('step3.top')}
                  </Link>
                  ,&nbsp;
                  <Link
                    target="_blank"
                    href={getGeneralTermsAndConditionsLink(orderDetails.market)}
                    underline={true}
                  >
                    {t('step3.gtc')}
                  </Link>
                  &nbsp;{t('step3.and')}&nbsp;
                  <Link
                    target="_blank"
                    href={getPrivacyPolicyLink(orderDetails.market)}
                    underline={true}
                  >
                    {t('step3.pp')}
                  </Link>
                </CheckboxLabel>
              </Checkbox>
            </FinalStepWrapper>
          </BoxContainer>
        </StepContainer>
      ),
    },
  ];

  return (
    <Wizard
      steps={wizardSteps}
      currentStep={step}
      onChangeStep={handleChangeStep}
      nextStepDisabled={nextStepDisabled}
      onSubmit={handleSubmit}
      submitButtonText={t('next.purchasesubscription')}
    />
  );
};
